'use client';

import { usePathname } from 'next/navigation';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { FeedbackFab } from '@/features/feedback/ui/FeedbackFab';
import { FeedbackProvider } from '@/features/feedback/ui/FeedbackProvider';
import { PanelProvider, usePanel } from '@/core/helpers/react/contexts/PanelContext';
import { LayoutModeProvider, useLayoutModeOptional } from '@/core/helpers/react/contexts/LayoutModeContext';
import { SidebarProvider, useSidebar } from '@/core/ui/primitives/sidebar';
import { HeadingsProvider, useHeadings } from '@/contexts/HeadingsContext';
import { NavScrollProvider } from '@/contexts/NavScrollContext';
import { AppNav } from '@core/ui/navigation/app-nav';
import { cn } from '@core/helpers/misc/GeneralUtils';
import { WIDE_BREAKPOINT, DESKTOP_BREAKPOINT } from '@core/helpers/styles/layoutBreakpoints';
import { lockBodyScroll, unlockBodyScroll } from '@/core/helpers/misc/ScrollLock';
import { useAppReadyOptional } from '@core/helpers/react/contexts/AppReadyContext';
import { FullPageLoader } from '@core/ui/loading/FullPageLoader';

/** Track which headings are currently visible in the scroll container */
function useVisibleHeadingsObserver() {
  const { headings, setVisibleHeadingIds } = useHeadings();

  useEffect(() => {
    if (!headings.length) return;

    const getHeadingElements = () => {
      return headings
        .map((h) => ({
          id: h.id,
          element: document.querySelector(`[data-outline-id="${h.id}"]`) || document.getElementById(h.id),
        }))
        .filter((h) => h.element) as { id: string; element: Element }[];
    };

    let lastVisibleIds: string[] = [];
    let ticking = false;

    const checkVisibility = () => {
      const headingElements = getHeadingElements();
      const visibleTop = 64;
      const visibleBottom = window.innerHeight * 0.8;

      const visibleIds: string[] = [];

      headingElements.forEach(({ id, element }) => {
        const rect = element.getBoundingClientRect();
        const isVisible = rect.top >= visibleTop && rect.top <= visibleBottom;
        if (isVisible) {
          visibleIds.push(id);
        }
      });

      if (visibleIds.length > 0) {
        const changed =
          visibleIds.length !== lastVisibleIds.length || visibleIds.some((id, i) => id !== lastVisibleIds[i]);
        if (changed) {
          lastVisibleIds = visibleIds;
          setVisibleHeadingIds(new Set(visibleIds));
        }
      }
      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(checkVisibility);
      }
    };

    const mainContent = document.getElementById('main-content');

    window.addEventListener('scroll', handleScroll, { passive: true, capture: true });

    if (mainContent) {
      mainContent.addEventListener('scroll', handleScroll, { passive: true });
    }

    setTimeout(checkVisibility, 100);

    return () => {
      window.removeEventListener('scroll', handleScroll, { capture: true });
      if (mainContent) {
        mainContent.removeEventListener('scroll', handleScroll);
      }
    };
  }, [headings, setVisibleHeadingIds]);
}

/** Responsive sidebar management - sets initial state based on viewport */
function useResponsivePanels() {
  const { leftPanel, navMenu } = usePanel();
  const sidebar = useSidebar();
  const { isMobile, setOpen } = sidebar;
  // setRightOpen may not exist in all sidebar contexts
  const setRightOpen = (sidebar as any).setRightOpen as ((open: boolean) => void) | undefined;
  const setOpenRef = useRef(setOpen);
  const setRightOpenRef = useRef(setRightOpen);
  const hasLeftContent = !!(leftPanel || navMenu);

  useEffect(() => {
    setOpenRef.current = setOpen;
    setRightOpenRef.current = setRightOpen;
  }, [setOpen, setRightOpen]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const applyLayout = () => {
      const width = window.innerWidth;
      if (width >= WIDE_BREAKPOINT) {
        // Wide: both panels expanded (only if there's content)
        setOpenRef.current(hasLeftContent);
        setRightOpenRef.current?.(true);
      } else if (width >= DESKTOP_BREAKPOINT) {
        // Desktop: collapse left (it's overlay), keep right
        setOpenRef.current(false);
        setRightOpenRef.current?.(true);
      } else {
        // Mobile: both sidebars closed by default (both are overlays)
        setOpenRef.current(false);
        setRightOpenRef.current?.(false);
      }
    };

    applyLayout();
    window.addEventListener('resize', applyLayout);
    return () => window.removeEventListener('resize', applyLayout);
  }, [isMobile, hasLeftContent]);
}

/**
 * AppShellContent - CSS-first responsive layout
 *
 * Layout tiers:
 * - Wide (≥1440px): Both sidebars in-flow grid columns [240px | content | 240px]
 * - Desktop (1024-1439px): Left sidebar = overlay, right sidebar = in-flow [content | 240px]
 * - Mobile (<1024px): Both sidebars = overlays (no grid)
 * - Nav menu behavior:
 *   - ≥1200px (nav-wide): Menu visible in navbar center
 *   - <1200px: Menu injected into left sidebar overlay
 */
function AppShellContent({ children }: { children: ReactNode }) {
  const [isReady, setIsReady] = useState(false);
  const appReady = useAppReadyOptional();
  useResponsivePanels();
  useVisibleHeadingsObserver();

  const { leftPanel, rightPanel, navMenu } = usePanel();
  const {
    open: leftOpenDesktop,
    openMobile: leftOpenMobile,
    isMobile,
    rightOpen: rightOpenDesktop,
    rightOpenMobile,
    setOpen: setLeftOpen,
    setOpenMobile: setLeftOpenMobile,
    setRightOpen: setRightOpenDesktop,
    setRightOpenMobile,
  } = useSidebar();
  // Use the correct state based on viewport
  const leftOpen = isMobile ? leftOpenMobile : leftOpenDesktop;
  const rightOpen = isMobile ? rightOpenMobile : rightOpenDesktop;
  // Use correct setter for right sidebar based on viewport
  const setRightOpen = isMobile ? setRightOpenMobile : setRightOpenDesktop;
  const layoutMode = useLayoutModeOptional();

  // Mark as ready after first render to prevent layout flash
  // Also signal 'layout' to the app ready context
  useEffect(() => {
    setIsReady(true);
    appReady?.signalReady('layout');
  }, [appReady]);

  // Lock body scroll when sidebar overlay is open to prevent scroll leak to main content
  // Only lock when sidebar is BOTH open AND in overlay mode (not when in-flow)
  useEffect(() => {
    const checkAndLockScroll = () => {
      const width = window.innerWidth;
      // Left sidebar is overlay below 1280px (WIDE_BREAKPOINT)
      const leftIsOverlay = width < 1280;
      // Right sidebar is overlay below 1024px (DESKTOP_BREAKPOINT)
      const rightIsOverlay = width < 1024;

      const shouldLock = (leftOpen && leftIsOverlay) || (rightOpen && rightIsOverlay);

      if (shouldLock) {
        lockBodyScroll();
      } else {
        unlockBodyScroll();
      }
    };

    checkAndLockScroll();
    window.addEventListener('resize', checkAndLockScroll);
    return () => {
      unlockBodyScroll();
      window.removeEventListener('resize', checkAndLockScroll);
    };
  }, [leftOpen, rightOpen]);

  // Show full-page loader until app is ready (graceful degradation if no context)
  const showLoader = appReady ? !appReady.isAppReady : false;

  return (
    <>
      <FullPageLoader isVisible={showLoader} />
      <div className={cn('app-shell w-full relative z-10', !isReady && 'opacity-0')}>
        <AppNav />

      {/* Layout Body - wrapper for grid background + content grid */}
      <div
        className={cn(
          'layout-body pt-16 min-h-screen relative',
          // Full-width mode: no max-width constraint (for marketing pages, welcome, etc.)
          // Normal mode: centered with user-adjustable max-width on wide screens
          !layoutMode?.fullWidth && 'wide:max-w-[var(--app-layout-width)] wide:mx-auto'
        )}>
        {/* Grid overlay - spans full width, scrolls with content */}
        <div
          className='absolute inset-x-0 top-0 h-[25vh] pointer-events-none z-0 bg-[linear-gradient(to_right,hsl(var(--muted-foreground)/0.15)_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--muted-foreground)/0.15)_1px,transparent_1px)] dark:bg-[linear-gradient(to_right,hsl(var(--muted-foreground)/0.2)_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--muted-foreground)/0.2)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:linear-gradient(to_bottom,#000_0%,transparent_100%)]'
          aria-hidden='true'
        />

        {/* Content Grid - responsive grid layout
            - Wide (≥1200px): both sidebars in-flow grid columns
            - Desktop (1024-1199px): right sidebar fixed, content centered with max-width preserving sidebar space
            - Mobile (<1024px): both sidebars are overlays */}
        <div
          className={cn(
            'layout-content relative w-full',
            // Desktop (1024-1279px): flexbox layout, content centered
            // When rightOpen: content aligned left with sidebar visible on right
            // When !rightOpen: content centered (sidebar hidden but space preserved in max-width)
            rightPanel && 'desktop-only:flex desktop-only:justify-end',
            rightPanel && !rightOpen && 'desktop-only:justify-center',
            // Wide (≥1280px): grid layout with fixed columns - content inside slides, not the columns
            (leftPanel || rightPanel) && 'wide:grid',
            // Wide with both panels: fixed 3-col [240px | content | 240px]
            leftPanel && rightPanel && 'wide:grid-cols-[240px_1fr_240px]',
            // Wide with left only: fixed 2-col [240px | content]
            leftPanel && !rightPanel && 'wide:grid-cols-[240px_1fr]',
            // Wide with right only: fixed 2-col [content | 240px]
            !leftPanel && rightPanel && 'wide:grid-cols-[1fr_240px]'
          )}>
          {/* Portal container for context menus.
              NOTE: Keep this as a tiny mount node (not a full-screen overlay), otherwise it can block pointer events. */}
          <div id='portal-container' className='fixed top-0 left-0 z-50' />

          {/* Actual sidebar content - renders when leftPanel OR navMenu exists */}
          {/* In-flow on wide (only when leftPanel), overlay on smaller screens */}
          {(leftPanel || navMenu) && (
            <aside
              id='panel-left'
              className={cn(
                'sidebar-left flex flex-col overflow-hidden',
                // Wide (≥1280px): in-flow grid column, fixed 240px - content inside slides
                leftPanel && 'wide:sticky wide:h-[calc(100vh-64px)] wide:top-16',
                // When only navMenu (no leftPanel): overlay below nav-wide, hidden above
                !leftPanel && 'nav-wide:hidden',
                // Below nav-wide: fixed overlay for navMenu (360px in overlay mode)
                !leftPanel &&
                  'max-nav-wide:w-[360px] max-nav-wide:fixed max-nav-wide:top-16 max-nav-wide:h-[calc(100vh-64px)] max-nav-wide:left-0 max-nav-wide:z-50',
                !leftPanel && (leftOpen ? 'max-nav-wide:translate-x-0' : 'max-nav-wide:-translate-x-full'),
                !leftPanel && 'max-nav-wide:transition-transform max-nav-wide:duration-300 max-nav-wide:ease-out',
                // Below wide (for pages with leftPanel): fixed overlay (360px in overlay mode)
                leftPanel &&
                  'max-wide:w-[360px] max-wide:fixed max-wide:top-16 max-wide:h-[calc(100vh-64px)] max-wide:left-0 max-wide:z-50',
                leftPanel && (leftOpen ? 'max-wide:translate-x-0' : 'max-wide:-translate-x-full'),
                leftPanel && 'max-wide:transition-transform max-wide:duration-300 max-wide:ease-out'
              )}>
              {/* Inner wrapper - slides in/out on wide, styled as overlay on smaller screens */}
              <div
                style={leftPanel ? { transition: 'translate 300ms ease-out, opacity 300ms ease-out' } : undefined}
                className={cn(
                  // NOTE: wide screens inject `navMenu` on scroll (headerHidden). We reserve a fixed slot (`wide:h-10`)
                  // so the left panel content doesn't jump, and reduce the wide top margin by the same amount.
                  'sidebar-left-inner flex flex-col flex-1 overflow-hidden mt-2 wide:mt-11 mb-2 ml-2',
                  // Wide (≥1280px): slide + fade animation
                  leftPanel &&
                    (leftOpen ? 'wide:translate-x-0 wide:opacity-100' : 'wide:-translate-x-[248px] wide:opacity-0'),
                  // Overlay styling for pages with leftPanel (below wide)
                  leftPanel && 'max-wide:mr-2 max-wide:bg-background max-wide:shadow-xl max-wide:rounded-md',
                  // Overlay styling for navMenu-only (below nav-wide)
                  !leftPanel &&
                    'max-nav-wide:mr-2 max-nav-wide:bg-background max-nav-wide:shadow-xl max-nav-wide:rounded-md'
                )}>
                {/* Nav menu section - injected when headerHidden (scroll-based) or below wide (responsive) */}
                {/* Wide: reserve a fixed slot (min-h-10) so injection doesn't push leftPanel. */}
                {/* When expanded, the menu must be in-flow so it pushes the vault tree down. */}
                <div className='sidebar-nav-menu wide:relative wide:min-h-10 wide:z-10'>
                  {navMenu && <div className='border-b border-border/50'>{navMenu}</div>}
                </div>
                {/* leftPanel content - wrapped with pt-20 only when navMenu is NOT present */}
                {leftPanel && (
                  <div className={cn('sidebar-left-panel flex-1 min-h-0 overflow-hidden')}>{leftPanel}</div>
                )}
              </div>
            </aside>
          )}

          {/* Main Content - z-[1] to be above grid background (z-0) */}
          <main
            id='main-content'
            className={cn(
              'main-content min-w-0 w-full overflow-y-auto relative z-[1]',
              // Desktop (1024-1279px): fixed width preserves sidebar space (100% - 240px)
              // !w-full removes the w-full so flex centering works
              rightPanel && 'desktop-only:!w-[calc(100%-240px)] desktop-only:shrink-0'
            )}>
            {children}
          </main>

          {/* Right Sidebar - only render when page declares rightPanel */}
          {rightPanel && (
            <aside
              id='panel-right'
              className={cn(
                'sidebar-right flex flex-col overflow-hidden',
                // Wide (≥1280px): in-flow grid column, fixed 240px - content inside slides
                'wide:sticky wide:h-[calc(100vh-64px)] wide:top-16',
                // Desktop-only (1024-1279px): in-flow, sticky, fixed width
                'desktop-only:w-[240px] desktop-only:sticky desktop-only:top-16 desktop-only:h-fit desktop-only:max-h-[calc(100vh-64px)]',
                'desktop-only:shrink-0',
                // Mobile (<1024px): fixed overlay
                'max-mobile:w-[240px] max-mobile:fixed max-mobile:top-16 max-mobile:h-[calc(100vh-64px)] max-mobile:right-0 max-mobile:z-50',
                rightOpen ? 'max-mobile:translate-x-0' : 'max-mobile:translate-x-full',
                'max-mobile:transition-transform max-mobile:duration-300 max-mobile:ease-out'
              )}>
              {/* Inner wrapper - slides in/out on wide/desktop, styled as overlay on mobile */}
              <div
                style={{ transition: 'translate 300ms ease-out, opacity 300ms ease-out' }}
                className={cn(
                  'sidebar-right-inner flex flex-col overflow-hidden mt-2 mb-2 mr-2',
                  // Wide (≥1280px): slide + fade animation
                  'wide:flex-1',
                  rightOpen ? 'wide:translate-x-0 wide:opacity-100' : 'wide:translate-x-[248px] wide:opacity-0',
                  // Desktop-only (1024-1279px): slide + fade animation
                  'desktop-only:h-auto',
                  rightOpen
                    ? 'desktop-only:translate-x-0 desktop-only:opacity-100'
                    : 'desktop-only:translate-x-[248px] desktop-only:opacity-0',
                  // Mobile: fill parent height, overlay styling
                  'max-mobile:flex-1 max-mobile:ml-2 max-mobile:bg-background max-mobile:shadow-xl max-mobile:rounded-md'
                )}>
                {rightPanel}
              </div>
            </aside>
          )}
        </div>
      </div>

      {/* Single shared backdrop for overlay sidebars
          - Wide (≥1440px): both sidebars in-flow, no backdrop needed
          - Desktop (1024-1439px): left is overlay, right is in-flow - backdrop for left only
          - Mobile (<1024px): both are overlays - backdrop for either
          - Nav-wide+ with only navMenu: hidden (navMenu is in navbar)
          z-35 puts it above main content (z-1) but below sidebars (z-50) */}
      <div
        className={cn(
          'fixed top-16 inset-x-0 bottom-0 z-[35] bg-black/30 backdrop-blur-sm',
          'transition-opacity duration-300 ease-out',
          // Prevent scroll on backdrop - touch-action:none stops touch scroll, overscroll-contain prevents scroll chaining
          'touch-none overscroll-contain',
          // Wide (≥1440px): hidden (both sidebars are in-flow)
          'wide:hidden',
          // Nav-wide (≥1200px): hidden for navMenu-only pages (navMenu is in navbar)
          !leftPanel && 'nav-wide:hidden',
          // Below nav-wide for navMenu-only: show when left sidebar open
          !leftPanel &&
            (leftOpen && navMenu
              ? 'max-nav-wide:opacity-100'
              : 'max-nav-wide:opacity-0 max-nav-wide:pointer-events-none'),
          // Desktop-only (1024-1439px) for pages with leftPanel: only left is overlay
          leftPanel &&
            (leftOpen ? 'desktop-only:opacity-100' : 'desktop-only:opacity-0 desktop-only:pointer-events-none'),
          // Mobile (<1024px): both sidebars are overlays, show when either is open
          leftPanel &&
            (leftOpen || (rightOpen && rightPanel)
              ? 'max-mobile:opacity-100'
              : 'max-mobile:opacity-0 max-mobile:pointer-events-none')
        )}
        // Prevent any scroll/touch events from leaking through backdrop
        onTouchMove={(e) => e.preventDefault()}
        onWheel={(e) => e.preventDefault()}
        onClick={() => {
          // Close overlay sidebars based on current viewport
          // Left sidebar is always overlay when backdrop is visible
          if (leftOpen && (leftPanel || navMenu)) {
            if (isMobile) {
              setLeftOpenMobile(false);
            } else {
              setLeftOpen(false);
            }
          }
          // Right sidebar is only overlay on mobile (<1024px)
          if (rightOpen && rightPanel && window.innerWidth < DESKTOP_BREAKPOINT) {
            setRightOpen(false);
          }
        }}
      />

      <FeedbackFab />
      </div>
    </>
  );
}

export default function ContentGate({ children }: { children: ReactNode }) {
  const pathname = usePathname() ?? '';

  if (pathname.startsWith('/auth')) {
    return <main className='gate-auth min-h-screen w-full flex items-center justify-center'>{children}</main>;
  }

  if (pathname.startsWith('/ext/')) {
    return (
      <FeedbackProvider>
        <main className='gate-standalone min-h-screen w-full overflow-y-auto'>{children}</main>
        <FeedbackFab />
      </FeedbackProvider>
    );
  }

  return (
    <FeedbackProvider>
      <HeadingsProvider>
        <NavScrollProvider>
          <LayoutModeProvider>
            <PanelProvider>
              <SidebarProvider className='gate-app-shell'>
                <AppShellContent>{children}</AppShellContent>
              </SidebarProvider>
            </PanelProvider>
          </LayoutModeProvider>
        </NavScrollProvider>
      </HeadingsProvider>
    </FeedbackProvider>
  );
}
